// Layouts e montagem das telas do tour de cameras (/ti/cameras). Logica pura,
// testada em camerasLayout.test.js.

// `destaque` = lado (em celulas) da camera grande no canto superior esquerdo.
export const LAYOUTS = [
  { id: '1', rotulo: '1 câmera', slots: 1, colunas: 1, linhas: 1 },
  { id: '4', rotulo: '2 × 2', slots: 4, colunas: 2, linhas: 2 },
  { id: '9', rotulo: '3 × 3', slots: 9, colunas: 3, linhas: 3 },
  { id: '16', rotulo: '4 × 4', slots: 16, colunas: 4, linhas: 4 },
  { id: '1+5', rotulo: '1 grande + 5', slots: 6, colunas: 3, linhas: 3, destaque: 2 },
  { id: '1+7', rotulo: '1 grande + 7', slots: 8, colunas: 4, linhas: 4, destaque: 3 },
];

// "Automatico": usa o layout do proprio tour (ou, por equipamento, o maximo de
// cameras por tela do cadastro).
export const LAYOUT_AUTO = 'auto';
const LAYOUT_PADRAO = '9';

export function layoutPorId(id) {
  return LAYOUTS.find((l) => l.id === id) || null;
}

// Grade de uma tela com `n` cameras. Layout fixo devolve ele mesmo; sem
// layout, o menor quadrado que cabe as cameras (8 cameras = 3x3).
export function gradeDaTela(layoutId, n) {
  const fixo = layoutPorId(layoutId);
  if (fixo) return fixo;
  const colunas = Math.max(1, Math.ceil(Math.sqrt(n)));
  return { id: LAYOUT_AUTO, slots: n, colunas, linhas: Math.max(1, Math.ceil(n / colunas)) };
}

// Ordem estavel: equipamento (ordem do cadastro), depois a camera.
function ordenarCameras(cameras, gravadoresPorId) {
  return [...cameras].sort((a, b) => {
    const ga = gravadoresPorId.get(a.gravador_id);
    const gb = gravadoresPorId.get(b.gravador_id);
    return (ga?.ordem ?? 0) - (gb?.ordem ?? 0)
      || String(ga?.nome).localeCompare(String(gb?.nome), 'pt-BR')
      || a.ordem - b.ordem
      || a.canal - b.canal;
  });
}

function fatiar(lista, tamanho) {
  const partes = [];
  for (let i = 0; i < lista.length; i += tamanho) partes.push(lista.slice(i, i + tamanho));
  return partes;
}

// O que da para assistir: todos os equipamentos, cada tour, cada grupo e cada
// filial que tem camera. A `chave` vai na URL (?ver=tour:3), entao cada TV
// pode abrir direto no seu tour.
export function fontesDoCatalogo(catalogo) {
  if (!catalogo) return [];
  const { cameras = [], gravadores = [], grupos = [], tours = [] } = catalogo;
  const fontes = [{ chave: 'todos', rotulo: 'Todos os equipamentos', tipo: 'todos' }];

  for (const t of tours) fontes.push({ chave: `tour:${t.id}`, rotulo: t.nome, tipo: 'tour' });

  const gruposComCamera = new Set(cameras.map((c) => c.grupo_id).filter(Boolean));
  for (const g of grupos) {
    if (gruposComCamera.has(g.id)) fontes.push({ chave: `grupo:${g.id}`, rotulo: g.nome, tipo: 'grupo' });
  }

  const filiais = [...new Set(gravadores.map((g) => g.filial).filter(Boolean))].sort();
  for (const f of filiais) fontes.push({ chave: `filial:${f}`, rotulo: `Filial ${f}`, tipo: 'filial' });

  return fontes;
}

// Sequencia de telas que o tour percorre para a fonte escolhida.
//   layoutEscolhido: LAYOUT_AUTO ou um id de LAYOUTS (sobrepoe o do tour).
// Devolve { telas, intervalo } — intervalo so vem quando o tour define um.
export function montarTelas(catalogo, chave, layoutEscolhido = LAYOUT_AUTO) {
  if (!catalogo) return { telas: [], intervalo: null };
  const gravadoresPorId = new Map(catalogo.gravadores.map((g) => [g.id, g]));
  const camerasPorId = new Map(catalogo.cameras.map((c) => [c.id, c]));
  const fixo = layoutPorId(layoutEscolhido);

  const telasDe = (titulo, cameras, layoutId, prefixo) => {
    const layout = fixo ? fixo.id : layoutId;
    const tamanho = (layoutPorId(layout) || { slots: Number(layout) || 9 }).slots;
    const partes = fatiar(cameras, tamanho);
    return partes.map((parte, i) => ({
      id: `${prefixo}:${layout}:${i}`,
      titulo,
      parte: i + 1,
      partes: partes.length,
      layout: layoutPorId(layout) ? layout : LAYOUT_AUTO,
      cameras: parte,
    }));
  };

  const [tipo, valor] = String(chave || 'todos').split(':');

  if (tipo === 'tour') {
    const tour = catalogo.tours.find((t) => String(t.id) === valor);
    if (!tour) return { telas: [], intervalo: null };
    const cameras = tour.camera_ids.map((id) => camerasPorId.get(id)).filter(Boolean);
    return { telas: telasDe(tour.nome, cameras, tour.layout, chave), intervalo: tour.intervalo };
  }

  if (tipo === 'grupo' || tipo === 'filial') {
    const filtro = tipo === 'grupo'
      ? (c) => String(c.grupo_id) === valor
      : (c) => gravadoresPorId.get(c.gravador_id)?.filial === valor;
    const titulo = tipo === 'grupo'
      ? catalogo.grupos.find((g) => String(g.id) === valor)?.nome || 'Grupo'
      : `Filial ${valor}`;
    const cameras = ordenarCameras(catalogo.cameras.filter(filtro), gravadoresPorId);
    return { telas: telasDe(titulo, cameras, LAYOUT_PADRAO, chave), intervalo: null };
  }

  // Todos os equipamentos: uma tela (ou mais) por DVR/NVR/camera, no maximo de
  // cameras por tela do cadastro de cada um.
  const telas = [];
  const gravadores = [...catalogo.gravadores].sort((a, b) => a.ordem - b.ordem
    || String(a.nome).localeCompare(String(b.nome), 'pt-BR'));
  for (const g of gravadores) {
    const cameras = ordenarCameras(catalogo.cameras.filter((c) => c.gravador_id === g.id), gravadoresPorId);
    if (!cameras.length) continue;
    telas.push(...telasDe(g.nome, cameras, String(g.por_tela), `todos:${g.id}`));
  }
  return { telas, intervalo: null };
}

// "Loja 03 · Caixa 01" quando a tela mistura equipamentos.
export function rotuloCompleto(camera, gravadoresPorId) {
  const g = gravadoresPorId.get(camera.gravador_id);
  return g ? `${g.nome} · ${camera.nome}` : camera.nome;
}
